import {
    DetailedHTMLProps,
    VideoHTMLAttributes,
    useEffect,
    useRef,
} from 'react';
import styled, { css } from 'styled-components6';
import { rwlImageGalleryStyles } from './utils';
import { MediaType } from 'rwl-shared/dist/utils/media';
import { Caption1 } from '../../theme';
import { When } from 'react-if';
import { IMAGE_ORIENTATION } from 'rwl-shared/dist/models/Media';
import { LeftArrow, RightArrow } from './ImageGalleryArrows';

const mediaStyle = css`
    max-width: 100%;
    max-height: 100%;
`;

const Media = styled.img`
    ${mediaStyle}
`;

const NavButtons = styled.span`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    height: inherit;
    width: 100%;
    position: absolute;
    pointer-events: none;
`;
const LeftNav = styled.span`
    width: 1em;
    position: absolute;
    left: 16px;
    cursor: pointer;
    pointer-events: auto;
`;
const RightNav = styled.span`
    width: 1em;
    position: absolute;
    right: 16px;
    cursor: pointer;
    pointer-events: auto;
`;

type VideoTagProps = {
    orientation?: IMAGE_ORIENTATION;
};
const VideoTag = styled.video<VideoTagProps>`
    width: ${({ orientation }) =>
        orientation === 'LANDSCAPE' ? '100vw' : 'auto'};

    height: ${({ orientation }) =>
        orientation === 'PORTRAIT' ? '75vh' : 'auto'};

    ${mediaStyle}
`;

type MediaVideoProps = {
    forcePause: boolean;
} & VideoTagProps &
    DetailedHTMLProps<VideoHTMLAttributes<HTMLVideoElement>, HTMLVideoElement>;
const MediaVideo = ({ forcePause, ...props }: MediaVideoProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    useEffect(() => {
        if (videoRef.current) {
            if (forcePause && !videoRef.current.paused) {
                videoRef.current.pause();
            }
        }
    }, [forcePause]);
    return <VideoTag ref={videoRef} {...props} />;
};

export const ModalGalleryContainer = styled.div`
    position: relative;
    overflow: hidden;
    flex: 1;
    width: 100%;

    ${rwlImageGalleryStyles}

    .image-gallery-svg {
        height: 5em;
    }

    button.image-gallery-bullet {
        padding: 5px;
    }

    button.image-gallery-left-nav,
    button.image-gallery-right-nav {
        height: 5em;
        width: 5em;
        & svg {
            width: 5em;
        }
    }
`;

const ImageContainer = styled.div`
    flex: 1 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`;

const ImageGalleryItem = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;

    @media (orientation: landscape) and (max-height: 744px) {
        flex-direction: row;
    }
`;

const ImageCaption = styled(Caption1)`
    padding: var(--rwl-spacing-md) var(--rwl-spacing-xl);
    max-width: 100%;
    overflow: hidden;
    white-space: initial;
    max-height: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (orientation: landscape) and (max-height: 744px) {
        max-height: 100%;
        max-width: 50%;
        align-items: flex-start;
        padding: 0 var(--rwl-spacing-xl);
    }

    @media (max-height: 400px) {
        display: none;
    }
`;

//Generic media type so we can use the same component for both PluginMedia and FeaturedMedia
export type ImageGalleryMediaItem = {
    id: string;
    type: MediaType;
    sourceUrl: string;
    thumbnailUrl?: string;
    orientation?: IMAGE_ORIENTATION;
    [key: string]: any;
};

type MediaImageGalleryProps = {
    onChange: (media: ImageGalleryMediaItem, index: number) => void;
    media: ImageGalleryMediaItem;
    allMedia: ImageGalleryMediaItem[];
    setMediaIndex: (index: number) => void;
    mediaIndex: number;
    caption?: string;
    onViewMedia?: (mediaId: string) => void;
};

export const MediaImageGallery = ({
    onChange,
    media,
    allMedia,
    setMediaIndex,
    mediaIndex,
    caption,
    onViewMedia,
}: MediaImageGalleryProps): JSX.Element | null => {
    const findAndSetSelectedMediaIndex = () => {
        const indexToUse = allMedia.findIndex(
            (mediaToRender) => mediaToRender.id === media.id
        );
        setMediaIndex(indexToUse !== -1 ? indexToUse : 0);
    };

    useEffect(() => {
        findAndSetSelectedMediaIndex();
        if (onViewMedia) {
            onViewMedia(media.id);
        }
    }, [media]);

    const item = allMedia[mediaIndex];
    if (!item) {
        return null;
    }

    return (
        <ModalGalleryContainer
            id={'mediaGallery'}
            onClick={(e: any) => e.stopPropagation()}
            data-theme={'dark'}
        >
            <ImageGalleryItem key={item.id}>
                <ImageContainer>
                    {item.type === MediaType.Photo ? (
                        <Media src={item.sourceUrl} loading="lazy" />
                    ) : (
                        <MediaVideo
                            orientation={item.orientation}
                            controls
                            disablePictureInPicture
                            controlsList={'nofullscreen nodownload'}
                            forcePause={media.sourceUrl !== item.sourceUrl}
                        >
                            <source src={item.sourceUrl} />
                        </MediaVideo>
                    )}
                </ImageContainer>
                {allMedia.length > 1 && (
                    <NavButtons>
                        <When condition={mediaIndex > 0}>
                            <LeftNav
                                id="left-nav"
                                onClick={() => {
                                    onChange(
                                        allMedia[mediaIndex - 1],
                                        mediaIndex - 1
                                    );
                                }}
                            >
                                <LeftArrow />
                            </LeftNav>
                        </When>
                        <When condition={mediaIndex < allMedia.length - 1}>
                            <RightNav
                                id="right-nav"
                                onClick={() => {
                                    onChange(
                                        allMedia[mediaIndex + 1],
                                        mediaIndex + 1
                                    );
                                }}
                            >
                                <RightArrow />
                            </RightNav>
                        </When>
                    </NavButtons>
                )}
                <When condition={!!caption}>
                    <ImageCaption>{caption}</ImageCaption>
                </When>
            </ImageGalleryItem>
        </ModalGalleryContainer>
    );
};
